import type React from 'react';
import { clsx } from 'clsx';
import type { PermissionKind } from '../steps/permissions-step';
import styles from './permission-glyphs.module.css';

export interface PermissionGlyphsProps {
  /** Which permissions have been granted. */
  granted: Record<PermissionKind, boolean>;
  /** Whether the glyphs should be visible (step === 'permissions'). */
  visible?: boolean;
}

// Tick checkmark SVG — inline SVG child, allowed per CLAUDE.md.
function TickIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function MicIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M5 10v1a7 7 0 0 0 14 0v-1" />
      <line x1="12" y1="19" x2="12" y2="22" />
    </svg>
  );
}

function AudioIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M11 5 6 9H2v6h4l5 4z" />
      <path d="M15.5 8.5a5 5 0 0 1 0 7" />
      <path d="M19 5a9 9 0 0 1 0 14" />
    </svg>
  );
}

function A11yIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="4" r="2" />
      <path d="M19 8.5c-2 1-4.5 1.5-7 1.5s-5-.5-7-1.5" />
      <path d="M12 10v5" />
      <path d="m9 21 3-6 3 6" />
    </svg>
  );
}

interface GlyphDef {
  kind: PermissionKind;
  label: string;
  Icon: React.FC;
}

const GLYPHS: GlyphDef[] = [
  { kind: 'microphone',    label: 'Mic',    Icon: MicIcon },
  { kind: 'system_audio', label: 'Audio',  Icon: AudioIcon },
  { kind: 'accessibility', label: 'Access', Icon: A11yIcon },
];

/**
 * Three glass glyphs that scale in on the permissions step and light up
 * (primary color + checkmark) when the corresponding permission is granted.
 */
export function PermissionGlyphs({ granted, visible = false }: PermissionGlyphsProps) {
  return (
    <div className={clsx(styles.glyphs, visible && styles.glyphsVisible)}>
      {GLYPHS.map(({ kind, label, Icon }) => {
        const isLit = granted[kind];
        return (
          <div
            key={kind}
            className={clsx(styles.glyph, isLit && styles.glyphLit)}
            data-lit={String(isLit)}
          >
            <span className={styles.glyphTick}>
              <TickIcon />
            </span>
            <span className={styles.glyphIcon}>
              <Icon />
            </span>
            <span className={styles.glyphLabel}>{label}</span>
          </div>
        );
      })}
    </div>
  );
}
